// Back gesture / Back button closes the top layer (a chat, Settings, a sheet) instead
// of leaving the app. Each open layer adds one browser history entry; Back pops it
// and closes that layer. A layer closed by its own button removes its entry again.
// In the iPhone app the WKWebView's edge swipe is Back (MainViewController.swift).
import { useEffect, useRef } from 'react';

const layers = [];
let ignorePops = 0;
// Entries are tagged with this page load and an increasing number. Entries left from
// before a reload belong to no open layer, so they count as the base.
const LOAD = Math.random().toString(36).slice(2);
let seq = 0;

if (typeof window !== 'undefined') {
  // Back closes every layer opened after the entry we land on. A forward move (the
  // iPhone's right-edge swipe) is undone rather than obeyed.
  window.addEventListener('popstate', (e) => {
    if (ignorePops > 0) { ignorePops -= 1; return; }
    const at = e.state?.mxmLoad === LOAD ? e.state.mxmSeq : 0;
    const top = layers[layers.length - 1];
    if (top && at > top.seq) { ignorePops += 1; window.history.back(); return; }
    while (layers.length && layers[layers.length - 1].seq > at) {
      const layer = layers.pop();
      layer.popped = true;
      layer.close();
    }
  });
}

// Opens a layer. Returns release(), to call when the layer closes by itself.
export const pushLayer = (close) => {
  const layer = { close, popped: false, seq: ++seq };
  layers.push(layer);
  window.history.pushState({ ...window.history.state, mxmLoad: LOAD, mxmSeq: layer.seq }, '');
  return () => {
    if (layer.popped) return;
    layer.popped = true;
    const i = layers.indexOf(layer);
    if (i >= 0) layers.splice(i, 1);
    ignorePops += 1;
    window.history.back();
  };
};

// While `open`, Back calls close().
export const useBackClose = (open, close) => {
  const closeRef = useRef(close);
  closeRef.current = close;
  useEffect(() => {
    if (!open) return;
    return pushLayer(() => closeRef.current());
  }, [open]);
};

// Every sheet (.modal-overlay, each with a .modal-close button) gets a layer while
// it's on screen, so Back closes it; on touch screens a swipe down closes it too.
export const watchSheets = (root = document.body) => {
  const open = new Map();
  const closeOf = el => () => el.querySelector('.modal-close')?.click();
  const sync = () => {
    const now = new Set(root.querySelectorAll('.modal-overlay'));
    now.forEach(el => { if (!open.has(el)) open.set(el, pushLayer(closeOf(el))); });
    [...open.keys()].forEach(el => { if (!now.has(el)) { open.get(el)(); open.delete(el); } });
  };
  const mo = new MutationObserver(sync);
  mo.observe(root, { childList: true, subtree: true });

  // Swipe down on a sheet that's scrolled to its top.
  let drag = null;
  const onStart = e => {
    const sheet = e.target.closest?.('.modal');
    if (!sheet || sheet.scrollTop > 0 || e.touches.length !== 1) return;
    drag = { sheet, y: e.touches[0].clientY, x: e.touches[0].clientX, dy: 0 };
  };
  const onMove = e => {
    if (!drag) return;
    const dy = e.touches[0].clientY - drag.y;
    const dx = Math.abs(e.touches[0].clientX - drag.x);
    if (dy < 0 || dx > dy) { drag.sheet.style.transform = ''; drag = null; return; }
    drag.dy = dy;
    drag.sheet.style.transform = `translateY(${dy}px)`;
  };
  const onEnd = () => {
    if (!drag) return;
    const { sheet, dy } = drag;
    drag = null;
    sheet.style.transform = '';
    if (dy > 90) sheet.closest('.modal-overlay')?.querySelector('.modal-close')?.click();
  };
  root.addEventListener('touchstart', onStart, { passive: true });
  root.addEventListener('touchmove', onMove, { passive: true });
  root.addEventListener('touchend', onEnd);
  root.addEventListener('touchcancel', onEnd);

  return () => {
    mo.disconnect();
    root.removeEventListener('touchstart', onStart);
    root.removeEventListener('touchmove', onMove);
    root.removeEventListener('touchend', onEnd);
    root.removeEventListener('touchcancel', onEnd);
    open.forEach(release => release());
  };
};
